import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn, formatCurrency } from "@/lib/utils";
import type { Position } from "@/solana/programs/types";
import { PositionsApi } from "@/backend/api-client";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { useAccountStore } from "@/backend/store";
import { useMarginPreview } from "@/backend/hooks/use-margin-preview";
import { toast } from "sonner";
import { Minus, Plus, X } from "lucide-react";

type EditPositionMarginModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position: Position | null;
  onSuccess?: () => void;
};

type TabMode = "increase" | "decrease";

const PERCENTAGE_OPTIONS = [10, 25, 50, 75, 100] as const;

export const EditPositionMarginModal = ({
  open,
  onOpenChange,
  position,
  onSuccess,
}: EditPositionMarginModalProps) => {
  const { userId } = useUnifiedAuth();
  const [marginAmount, setMarginAmount] = useState<string>("");
  const [tabMode, setTabMode] = useState<TabMode>("increase");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const balance = useAccountStore((state) => state.balance);
  
  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setMarginAmount("");
      setTabMode("increase");
    }
  }, [open]);
  
  const currentMargin = position?.margin || 0;
  const marginAmountNum = Number(marginAmount) || 0;
  const availableMargin = balance?.availableMargin || 0;
  
  // Calculate actual delta based on tab mode
  const marginDelta = tabMode === "increase" ? marginAmountNum : -marginAmountNum;
  const newMargin = currentMargin + marginDelta;
  
  // Max amounts for each mode
  const maxIncrease = availableMargin;
  const maxDecrease = Math.max(0, currentMargin - 1); // Must leave at least $1 margin
  const maxAmount = tabMode === "increase" ? maxIncrease : maxDecrease;
  
  // Validation
  const isValidAmount = marginAmountNum > 0 && marginAmountNum <= maxAmount;
  const hasEnoughForDecrease = tabMode === "decrease" ? newMargin >= 1 : true;
  const isValid = isValidAmount && hasEnoughForDecrease && marginAmount !== "";
  
  // Get real-time liquidation price preview from backend
  const { preview } = useMarginPreview({
    userId,
    market: position?.market || "",
    marginDelta,
    enabled: open && marginAmountNum > 0 && !!position,
  });
  
  // Use backend-calculated liquidation price from preview
  const newLiquidationPrice = preview?.new_liquidation_price 
    ? parseFloat(preview.new_liquidation_price)
    : position?.liquidationPrice || 0;
  
  // Handle percentage button clicks
  const handlePercentageClick = useCallback((percentage: number) => {
    const amount = (maxAmount * percentage) / 100;
    setMarginAmount(amount.toFixed(2));
  }, [maxAmount]);
  
  // Handle +/- buttons
  const handleIncrement = useCallback(() => {
    const current = marginAmountNum || 0;
    const newAmount = Math.min(current + 1, maxAmount);
    setMarginAmount(newAmount.toFixed(2));
  }, [marginAmountNum, maxAmount]);
  
  const handleDecrement = useCallback(() => {
    const current = marginAmountNum || 0;
    const newAmount = Math.max(current - 1, 0);
    setMarginAmount(newAmount > 0 ? newAmount.toFixed(2) : "");
  }, [marginAmountNum]);
  
  const handleSubmit = useCallback(async () => {
    if (!userId || !position || !isValid) return;
    
    setIsSubmitting(true);
    try {
      const response = await PositionsApi.updatePositionMargin(
        position.market,
        marginDelta
      );
      
      // Sync updated position to store
      if (response.position) {
        useAccountStore.getState().updatePositionFromHttp(response.position);
      }
      
      toast.success(response.message);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to adjust margin";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [userId, position, marginDelta, isValid, onOpenChange, onSuccess]);
  
  if (!position) return null;
  
  const isLong = position.side === "long";
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] p-0 gap-0 overflow-hidden bg-card border-border" showCloseButton={false}>
        {/* Header */}
        <DialogHeader className="px-7 pt-7 pb-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-lg font-semibold">
                Adjust Margin
              </DialogTitle>
              <span className={cn(
                "text-xs font-medium px-2 py-0.5 rounded",
                isLong ? "bg-bid/20 text-bid-foreground" : "bg-ask/20 text-ask-foreground"
              )}>
                {position.market} {isLong ? "Long" : "Short"}
              </span>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </DialogHeader>
        
        {/* Tabs */}
        <div className="px-7 pt-6">
          <div className="flex border-b border-border">
            <button
              onClick={() => {
                setTabMode("increase");
                setMarginAmount("");
              }}
              className={cn(
                "flex-1 pb-3 text-sm font-medium transition-colors relative",
                tabMode === "increase" 
                  ? "text-foreground" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Increase(+)
              {tabMode === "increase" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-buy" />
              )}
            </button>
            <button
              onClick={() => {
                setTabMode("decrease");
                setMarginAmount("");
              }}
              className={cn(
                "flex-1 pb-3 text-sm font-medium transition-colors relative",
                tabMode === "decrease" 
                  ? "text-foreground" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Decrease(-)
              {tabMode === "decrease" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sell" />
              )}
            </button>
          </div>
        </div>
        
        <div className="px-7 pt-6 pb-7 space-y-5">
          {/* Input Label */}
          <div>
            <label className="text-sm text-muted-foreground block mb-3">
              {tabMode === "increase" ? "Increase" : "Decrease"} Margin ($)
            </label>
            
            {/* Input with +/- buttons */}
            <div className="flex items-center bg-muted/50 rounded-lg border border-border overflow-hidden">
              <input
                type="text"
                inputMode="decimal"
                value={marginAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty, or valid decimal numbers
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    setMarginAmount(value);
                  }
                }}
                placeholder="Enter at least $1"
                className="flex-1 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-muted-foreground/60"
              />
              <div className="flex items-center border-l border-border">
                <button
                  onClick={handleDecrement}
                  disabled={marginAmountNum <= 0}
                  className="px-3 py-3 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <div className="w-px h-5 bg-border" />
                <button
                  onClick={handleIncrement}
                  disabled={marginAmountNum >= maxAmount}
                  className="px-3 py-3 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Percentage Buttons */}
          <div className="flex gap-2">
            {PERCENTAGE_OPTIONS.map((pct) => (
              <button
                key={pct}
                onClick={() => handlePercentageClick(pct)}
                className={cn(
                  "flex-1 py-1.5 text-xs font-medium rounded border transition-colors",
                  marginAmountNum > 0 && Math.abs(marginAmountNum - (maxAmount * pct / 100)) < 0.01
                    ? "border-buy bg-buy/10 text-buy"
                    : "border-border bg-muted/30 text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                )}
              >
                {pct}%
              </button>
            ))}
          </div>
          
          {/* Info Rows */}
          <div className="space-y-2.5 pt-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Initial Margin</span>
              <span className="font-medium">${formatCurrency(currentMargin, 2)}</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Max. {tabMode === "increase" ? "Increase" : "Decrease"}
              </span>
              <span className="font-medium">${formatCurrency(maxAmount, 2)}</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Liq. Price</span>
              <span className="font-medium">
                ${formatCurrency(position.liquidationPrice, 2)}
              </span>
            </div>
            
            {/* Always show this row to prevent modal expansion - display dash when no value entered */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Liq. price after margin {tabMode === "increase" ? "increase" : "decrease"}
              </span>
              <span className={cn(
                "font-medium",
                marginAmountNum > 0 && tabMode === "increase" 
                  ? (isLong ? "text-bid-foreground" : "text-ask-foreground")
                  : marginAmountNum > 0 && tabMode === "decrease"
                  ? (isLong ? "text-ask-foreground" : "text-bid-foreground")
                  : "text-muted-foreground"
              )}>
                {marginAmountNum > 0 ? `$${formatCurrency(newLiquidationPrice, 2)}` : "—"}
              </span>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 h-11"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 h-11 bg-buy hover:bg-buy/90 text-white"
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? "Processing..." : "Confirm"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
